import dayjs from "dayjs";

function pickFirst(source, keys, fallback = "") {
  if (!source) return fallback;
  for (const key of keys) {
    const value = source[key];
    if (value != null && value !== "") return value;
  }
  return fallback;
}

function formatDateLabel(value) {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(value);
}

function formatTravelers(item) {
  const adults = Number(item.adult ?? item.adults ?? item.adultCount ?? 0);
  const children = Number(item.child ?? item.children ?? item.childCount ?? 0);
  const infants = Number(item.infant ?? item.infants ?? item.infantCount ?? 0);
  const parts = [];

  if (adults > 0) parts.push(`${adults} Adult${adults > 1 ? "s" : ""}`);
  if (children > 0) parts.push(`${children} Child${children > 1 ? "ren" : ""}`);
  if (infants > 0) parts.push(`${infants} Infant${infants > 1 ? "s" : ""}`);

  const fallback = pickFirst(item, ["travelers", "travelerText", "guestText"]);
  return parts.length ? parts.join(", ") : fallback;
}

function formatTravelDates(item) {
  const direct = pickFirst(item, ["travelDates", "travelDateText", "dateRange"]);
  if (direct) return direct;

  const from = pickFirst(item, [
    "fromDate",
    "travelDate",
    "checkInDate",
    "startDate",
    "travelStartDate",
  ]);
  const to = pickFirst(item, [
    "toDate",
    "checkOutDate",
    "endDate",
    "travelEndDate",
  ]);

  if (from && to) {
    return `${formatDateLabel(from)} - ${formatDateLabel(to)}`;
  }

  return formatDateLabel(from || to) || "";
}

function flattenCartModules(items = []) {
  if (!Array.isArray(items) || !items.length) return [];

  const isModuleGroup = items.every(
    (entry) =>
      entry &&
      typeof entry === "object" &&
      Array.isArray(entry.data) &&
      typeof entry.module === "string"
  );

  if (isModuleGroup) {
    return items.flatMap((entry) =>
      entry.data.map((item) => ({
        ...item,
        module: item.module || entry.module,
      }))
    );
  }

  return items;
}

function resolveCartItemsSource(response) {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) {
    return flattenCartModules(payload);
  }

  if (payload && typeof payload === "object") {
    for (const key of ["cartItems", "items", "cartList", "data"]) {
      const candidate = payload[key];
      if (Array.isArray(candidate)) {
        const flattened = flattenCartModules(candidate);
        if (flattened.length) return flattened;
      }
    }
  }

  return [];
}

function resolveInclusion(item) {
  const direct = pickFirst(item, ["inclusion", "inclusions", "inclusionText"]);
  if (direct) return direct;

  const nested = item?.inclusionExclusion?.inclusion;
  return nested ? String(nested) : "";
}

function resolveSubtitle(item) {
  const cities = pickFirst(item, ["citys", "cities", "cityText", "destinationText"]);
  const optionName = pickFirst(item, ["optionName", "packageOption", "roomType"]);
  const location = pickFirst(item, ["subtitle", "location"]);

  if (cities && optionName) return `${cities} | ${optionName}`;
  return cities || optionName || location || "";
}

export function normalizeCartItem(item = {}, index = 0) {
  const moduleType = pickFirst(item, ["module", "moduleType", "moduleName", "type"]);
  const unitPrice = Number(
    pickFirst(item, ["unitPrice", "pricePerPerson", "adultPrice", "basePrice"], 0)
  );
  const quantity = Number(
    pickFirst(item, ["quantity", "travelerCount", "adult", "adults"], 1) || 1
  );
  const totalPrice = Number(
    pickFirst(item, ["totalPrice", "totalAmount", "grandPrice", "amount"], 0) ||
      unitPrice * quantity
  );
  const saveAmount = Number(
    pickFirst(
      item,
      ["saveAmount", "savedAmount", "discountAmount", "youSave", "savingAmount"],
      0
    )
  );

  const title = pickFirst(item, [
    "title",
    "packageName",
    "hotelName",
    "activityName",
    "visaName",
    "name",
    "serviceName",
  ]);

  const moduleKey = String(moduleType || "").toLowerCase();
  const inclusion =
    moduleKey === "holiday" ? resolveHolidayInclusions(item) : resolveInclusion(item);

  return {
    id: pickFirst(item, ["CartId", "cartId", "id", "cartItemId", "tempBookingId"], index),
    slug: pickFirst(item, ["slug", "packageSlug", "activitySlug", "holidaySlug"]),
    couponCode: pickFirst(item, ["couponCode", "coupon"]),
    moduleType,
    moduleKey,
    title: typeof title === "string" ? title.trim() : title,
    subtitle: resolveSubtitle(item),
    image: pickFirst(item, [
      "image",
      "imageUrl",
      "imageURL",
      "imagePath",
      "thumbnail",
      "packageImage",
      "mobImagePath",
    ]),
    duration: pickFirst(item, ["duration", "durationText", "nightsText"]),
    inclusion,
    travelDates: formatTravelDates(item),
    checkIn: formatDateLabel(
      pickFirst(item, ["checkIn", "checkInDate", "checkInText", "travelStartDate"])
    ),
    checkOut: formatDateLabel(
      pickFirst(item, ["checkOut", "checkOutDate", "checkOutText", "travelEndDate"])
    ),
    nights: pickFirst(item, ["nights", "nightsText", "nightText"]),
    guestsRooms: pickFirst(item, ["guestsRooms", "guestText", "roomText", "optionName"]),
    travelers: formatTravelers(item),
    unitPrice,
    quantity,
    totalPrice,
    saveAmount,
    selected: item.selected !== false,
    raw: item,
  };
}

export function normalizeCartResponse(response) {
  const status = String(response?.status || "").toLowerCase();
  const message = response?.message || "";

  if (status !== "success") {
    return {
      items: [],
      grandTotal: 0,
      saveAmount: 0,
      message,
      isEmpty: true,
    };
  }

  const itemsSource = resolveCartItemsSource(response);
  const items = itemsSource.map(normalizeCartItem);

  const payload = response?.data ?? response;

  const grandTotal = Number(
    pickFirst(payload, ["grandTotal", "totalAmount", "totalPrice", "cartTotal"], 0) ||
      items.reduce((sum, item) => sum + (item.selected ? item.totalPrice : 0), 0)
  );

  const saveAmount = Number(
    pickFirst(payload, ["saveAmount", "totalSave", "savedAmount", "discountAmount"], 0) ||
      items.reduce((sum, item) => sum + (item.selected ? item.saveAmount : 0), 0)
  );

  const isEmpty = !items.length || /empty/i.test(message);

  return {
    items,
    grandTotal,
    saveAmount,
    message,
    isEmpty,
  };
}

export function isHotelItem(item) {
  const type = String(item?.moduleType || "").toLowerCase();
  return type.includes("hotel") || type.includes("stay");
}

export function isActivityItem(item) {
  return String(item?.moduleType || "").toLowerCase().includes("activ");
}

export function isHolidayItem(item) {
  return String(item?.moduleType || "").toLowerCase().includes("holiday");
}

const HOLIDAY_SERVICE_LABELS = [
  { key: "isFlightIncluded", label: "Flight" },
  { key: "isMealsIncluded", label: "Meals" },
  { key: "isTransferIncluded", label: "Transfer" },
  { key: "isHotelIncluded", label: "Hotel" },
  { key: "isActivityIncluded", label: "Activity" },
  { key: "isVisaIncluded", label: "VISA" },
];

export function resolveHolidayInclusions(item = {}) {
  const labels = HOLIDAY_SERVICE_LABELS.filter(
    (service) => item[service.key] === true || item[service.key] === "true"
  ).map((service) => service.label);

  if (labels.length) return labels.join(", ");
  return resolveInclusion(item);
}

const MODULE_DETAIL_BASE = {
  holiday: "/holiday",
  activity: "/activity",
  staycation: "/stays",
  visa: "/visas",
  hotel: "/stays",
};

const MODULE_BOOKING_PATH = {
  holiday: "/holiday/package-booking",
  activity: "/activity/package-booking",
};

const MODULE_COOKIE_NAME = {
  holiday: "Holiday",
  activity: "Activity",
  staycation: "Staycation",
  visa: "Visa",
  hotel: "Hotel",
};

export function getCartModuleKey(item = {}) {
  return String(item.moduleType || item.raw?.module || "").toLowerCase();
}

export function getCartDetailPath(item = {}) {
  const moduleKey = getCartModuleKey(item);
  const slug = item.slug || pickFirst(item.raw, ["slug"]);
  const base = MODULE_DETAIL_BASE[moduleKey];
  if (!base || !slug) return null;
  return `${base}/${slug}`;
}

export function getCartBookingPath(item = {}, encryptedCouponCode = "") {
  const moduleKey = getCartModuleKey(item);
  const base = MODULE_BOOKING_PATH[moduleKey];
  if (!base) return null;
  if (encryptedCouponCode) {
    return `${base}?couponCode=${encodeURIComponent(encryptedCouponCode)}`;
  }
  return base;
}

export function getCartModuleCookieValue(item = {}) {
  return MODULE_COOKIE_NAME[getCartModuleKey(item)] || "";
}

export function canBookCartItem(item = {}) {
  return Boolean(getCartBookingPath(item));
}
